let Cesium: any = null;
try {
  Cesium = require('cesium');
} catch {
  try {
    Cesium = (globalThis as any).Cesium || null;
  } catch {
    Cesium = null;
  }
}

export interface MITerrainProviderOptions {
  url: string;
  requestVertexNormals?: boolean;
  requestWaterMask?: boolean;
  credit?: string;
}

export interface MITilesetProviderOptions {
  url: string;
  maximumScreenSpaceError?: number;
  show?: boolean;
  maximumMemoryUsage?: number;
  skipLevelOfDetail?: boolean;
}

export interface MIImageryProviderOptions {
  url: string;
  type?: 'wms' | 'wmts' | 'tms' | 'xyz' | 'single-image';
  layers?: string;
  format?: string;
  tileWidth?: number;
  tileHeight?: number;
  minimumLevel?: number;
  maximumLevel?: number;
  credit?: string;
  subdomains?: string[];
}

export const MAPMYINDIA_TERRAIN = {
  india: 'https://mgis3d.mapmyindia.in/tilesets/tiles/layer.json',
  world: 'https://mgis3d.mapmyindia.in/tilesetsWorld/tiles/layer.json',
} as const;

export class MITerrainProvider {
  static async mapMyIndiaIndia(): Promise<any> {
    return MITerrainProvider.fromUrl({ url: MAPMYINDIA_TERRAIN.india, credit: 'MapMyIndia' });
  }

  static async mapMyIndiaWorld(): Promise<any> {
    return MITerrainProvider.fromUrl({ url: MAPMYINDIA_TERRAIN.world, credit: 'MapMyIndia' });
  }

  static async fromUrl(options: MITerrainProviderOptions): Promise<any> {
    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    try {
      const provider = await Cesium.CesiumTerrainProvider.fromUrl(options.url, {
        requestVertexNormals: options.requestVertexNormals ?? true,
        requestWaterMask: options.requestWaterMask ?? false,
        credit: options.credit,
      });
      return provider;
    } catch (err) {
      throw new Error(`Failed to load terrain from ${options.url}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  static flat(): any {
    if (!Cesium) {
      throw new Error('3D engine is not available.');
    }
    return new Cesium.EllipsoidTerrainProvider();
  }
}

export class MITilesetProvider {
  static async fromUrl(options: MITilesetProviderOptions): Promise<any> {
    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    try {
      const tileset = await Cesium.Cesium3DTileset.fromUrl(options.url, {
        maximumScreenSpaceError: options.maximumScreenSpaceError ?? 16,
        show: options.show ?? true,
        maximumMemoryUsage: options.maximumMemoryUsage,
        skipLevelOfDetail: options.skipLevelOfDetail,
      });
      return tileset;
    } catch (err) {
      throw new Error(`Failed to load 3D tileset from ${options.url}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

export class MIImageryProvider {
  static fromUrl(options: MIImageryProviderOptions): any {
    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    switch (options.type) {
      case 'wms':
        return new Cesium.WebMapServiceImageryProvider({
          url: options.url,
          layers: options.layers || '',
          parameters: { format: options.format || 'image/png', transparent: true },
          credit: options.credit,
        });

      case 'wmts':
        return new Cesium.WebMapTileServiceImageryProvider({
          url: options.url,
          layer: options.layers || '',
          style: 'default',
          format: options.format || 'image/png',
          tileMatrixSetID: 'default',
          credit: options.credit,
        });

      case 'tms':
        return new Cesium.TileMapServiceImageryProvider({
          url: options.url,
          minimumLevel: options.minimumLevel ?? 0,
          maximumLevel: options.maximumLevel ?? 18,
          credit: options.credit,
        });

      case 'single-image':
        return new Cesium.SingleTileImageryProvider({
          url: options.url,
          credit: options.credit,
        });

      case 'xyz':
      default:
        return new Cesium.UrlTemplateImageryProvider({
          url: options.url,
          minimumLevel: options.minimumLevel ?? 0,
          maximumLevel: options.maximumLevel ?? 18,
          subdomains: options.subdomains,
          credit: options.credit,
        });
    }
  }
}
